<template>
  <!-- 客户分析报表：客户类型/来源/行业结构与增长趋势 -->
  <report-layout active="customer">
    <list-page>
      <search-condition @search="resetSearch" :show-search="true" page-name="customerReport">
        <template #default>
          <yq-date-range v-model:start-time="query.startTime" v-model:end-time="query.endTime" class="double-width">
            <template #prefix>创建日期</template>
          </yq-date-range>
          <el-select v-model="query.customerType" placeholder="请选择" clearable>
            <template #prefix>客户类型</template>
            <el-option v-for="d in customerTypeList" :key="d.userCode" :label="d.dictName" :value="d.userCode"/>
          </el-select>
          <el-select v-model="query.region" placeholder="请选择" clearable filterable>
            <template #prefix>归属区域</template>
            <el-option v-for="p in provinceList" :key="p.id" :label="p.name" :value="p.id"/>
          </el-select>
          <el-input v-model="query.searchKey" placeholder="请输入客户名称" clearable @keydown.enter="resetSearch">
            <template #prefix>客户名称</template>
          </el-input>
        </template>
        <template #option>
          <page-button @click="resetAll">重置</page-button>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
        </template>
      </search-condition>

      <list-table :show-shadow="false">
        <div v-loading="loading" class="report-body">
          <!-- 客户数据分布 -->
          <div id="rp-customer-distribution" class="chart-card" ref="sectionDistribution">
            <div class="section-title">客户数据分布</div>
            <div class="donut-wrap">
              <div ref="typeChart" class="donut-div"></div>
              <div class="donut-legend">
                <div v-for="row in typeRows" :key="row.name" class="lg-row">
                  <span class="lg-dot" :style="{background: row.color}"></span>
                  <span class="lg-name" :title="row.name">{{ row.name }}</span>
                  <span class="lg-val">{{ row.value }}</span>
                  <span class="lg-ratio">{{ row.ratio }}%</span>
                </div>
                <div v-if="!typeRows.length" class="lg-empty">暂无数据</div>
              </div>
            </div>
            <div class="divider-t"><span>统计明细列表</span></div>
            <el-table :data="typeRows" border size="small" show-summary :summary-method="summaryMethod" empty-text="暂无数据">
              <el-table-column label="序号" width="70" align="center">
                <template v-slot="{$index}">{{ $index + 1 }}</template>
              </el-table-column>
              <el-table-column label="客户类型" prop="name">
                <template v-slot="{row}">
                  <span class="lg-dot inline" :style="{background: row.color}"></span>{{ row.name }}
                </template>
              </el-table-column>
              <el-table-column label="客户数量" prop="value" align="right" width="140">
                <template v-slot="{row}"><span class="num bold">{{ row.value }}</span></template>
              </el-table-column>
              <el-table-column label="客户数量占比" prop="ratio" align="right" width="160">
                <template v-slot="{row}"><span class="num">{{ row.ratio }}%</span></template>
              </el-table-column>
            </el-table>
          </div>

          <div class="grid-2">
            <!-- 客户来源分析 -->
            <div id="rp-customer-source" class="chart-card" ref="sectionSource">
              <div class="section-title">客户来源分析</div>
              <div class="donut-wrap">
                <div ref="sourceChart" class="donut-div"></div>
                <div class="donut-legend">
                  <div v-for="row in sourceRows" :key="row.name" class="lg-row">
                    <span class="lg-dot" :style="{background: row.color}"></span>
                    <span class="lg-name" :title="row.name">{{ row.name }}</span>
                    <span class="lg-val">{{ row.value }}</span>
                    <span class="lg-ratio">{{ row.ratio }}%</span>
                  </div>
                  <div v-if="!sourceRows.length" class="lg-empty">暂无数据</div>
                </div>
              </div>
            </div>
            <!-- 客户行业分析 -->
            <div id="rp-customer-industry" class="chart-card" ref="sectionIndustry">
              <div class="section-title">客户行业分析</div>
              <div ref="industryChart" class="hbar-div"></div>
            </div>
          </div>

          <!-- 客户增长趋势 -->
          <div class="chart-card">
            <div class="section-title">客户增长趋势</div>
            <div ref="trendChart" class="chart-div"></div>
          </div>
        </div>
      </list-table>
    </list-page>
  </report-layout>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import ReportLayout from "@/views/h5/crm/report/ReportLayout.vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import YqDateRange from "@/components/h5/date/YqDateRange.vue";
import customerStatApi from "@/api/dz-crm/partner/customerStatApi";
import commonApi from "@/api/commonApi";
import {DICT_CUSTOMER_TYPE, DICT_INDUSTRY_TYPE, DICT_PARTNER_ORIGIN} from "@/views/h5/crm/crmDictionary";
import {PROVINCE_LIST, toChartRows} from "@/views/h5/crm/customerStat";
import {cleanParams, donutOption, hBarOption, lineOption} from "@/views/h5/crm/report/reportUtil";

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default {
  name: "CustomerReport",
  components: {ReportLayout, ListPage, ListTable, SearchCondition, PageButton, YqDateRange},
  data() {
    const year = new Date().getFullYear();
    return {
      loading: false,
      query: {startTime: `${year}-01-01`, endTime: today(), customerType: "", region: "", searchKey: ""},
      overview: {},
      customerTypeList: [],
      industryList: [],
      sourceList: [],
      provinceList: PROVINCE_LIST,
      typeChart: null,
      sourceChart: null,
      industryChart: null,
      trendChart: null,
      dictReady: false,
      dataReady: false,
    };
  },
  computed: {
    typeRows() {
      return toChartRows(this.overview.typeStats, this.customerTypeList);
    },
    sourceRows() {
      return toChartRows(this.overview.sourceStats, this.sourceList);
    },
    industryRows() {
      return toChartRows(this.overview.industryStats, this.industryList);
    },
    sourceTotal() {
      return this.sourceRows.reduce((sum, row) => sum + row.value, 0);
    },
  },
  beforeRouteLeave(to, from, next) {
    next();
  },
  mounted() {
    Promise.all([
      commonApi.queryUniqueDictionaryList(DICT_CUSTOMER_TYPE),
      commonApi.queryUniqueDictionaryList(DICT_INDUSTRY_TYPE),
      commonApi.queryUniqueDictionaryList(DICT_PARTNER_ORIGIN),
    ]).then(([customerType, industry, source]) => {
      this.customerTypeList = customerType || [];
      this.industryList = industry || [];
      this.sourceList = source || [];
      this.dictReady = true;
      this.renderIfReady();
    });
    this.loadReport();
  },
  unmounted() {
    [this.typeChart, this.sourceChart, this.industryChart, this.trendChart]
        .forEach(chart => chart && chart.dispose());
  },
  methods: {
    async loadReport() {
      this.loading = true;
      try {
        const params = cleanParams({
          ...this.query,
          startTime: this.query.startTime ? this.query.startTime.slice(0, 10) : "",
          endTime: this.query.endTime ? this.query.endTime.slice(0, 10) : "",
        });
        this.overview = await customerStatApi.overview(params) || {};
        this.dataReady = true;
        this.$nextTick(() => this.renderIfReady());
      } finally {
        this.loading = false;
      }
    },
    resetSearch() {
      this.loadReport();
    },
    resetAll() {
      const year = new Date().getFullYear();
      this.query = {startTime: `${year}-01-01`, endTime: today(), customerType: "", region: "", searchKey: ""};
      this.loadReport();
    },
    renderIfReady() {
      if (this.dictReady && this.dataReady) {
        this.renderCharts();
      }
    },
    renderCharts() {
      this.typeChart = this.setOption(this.typeChart, this.$refs.typeChart,
          donutOption(this.typeRows, this.overview.customerCount || 0, "客户数量"));
      this.sourceChart = this.setOption(this.sourceChart, this.$refs.sourceChart,
          donutOption(this.sourceRows, this.sourceTotal, "客户数量"));
      this.industryChart = this.setOption(this.industryChart, this.$refs.industryChart,
          hBarOption(this.industryRows, "#1677ff", 10));
      this.trendChart = this.setOption(this.trendChart, this.$refs.trendChart,
          lineOption(this.overview.trendMonthList || [], this.overview.trendCountList || [], "#1677ff"));
    },
    setOption(chart, dom, option) {
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      chart.setOption(option, true);
      return chart;
    },
    summaryMethod({columns}) {
      return columns.map((column, index) => {
        if (index === 0) {
          return "合计";
        }
        if (column.property === "value") {
          return String(this.overview.customerCount || 0);
        }
        if (column.property === "ratio") {
          return this.typeRows.length ? "100.00%" : "0.00%";
        }
        return "";
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/views/h5/crm/report/report.scss";

.divider-t {
  display: flex;
  align-items: center;
  margin: 18px 0 10px;
  font-size: 13px;
  color: #909399;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: var(--yq-border-color, #ebeef5);
  }

  span {
    padding: 0 10px;
  }
}

.lg-dot.inline {
  display: inline-block;
  margin-right: 6px;
}
</style>
